import React, { FC, useCallback, useMemo, ReactElement, useState } from 'react';
import { connect } from 'react-redux';
import { nullSafeGet, nullSafeGetOrElse, renderCurrency } from '../../utils/DataAccessUtils';
import { Button, Card, Col, Layout, Row } from 'antd';
import { HorizontalKeyValueDisplay } from '../horizontal_key_value_display/HorizontalKeyValueDisplay';

import { withRouter } from 'react-router';
import { EditOutlined } from '@ant-design/icons';
import EditSerialNumberModal from './EditSerialNumberModal';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import { EmptyState } from '../empty_state/EmptyState';
import { assetHistoryRestCrudThunksForSupplier } from '../../thunks/asset_history_thunks';
import { DATE_FORMAT, dateFormatter } from '../../utils/DataFormatterUtils';
import AssetCardDisplay from '../asset_card_display/AssetCardDisplay';
import HyperLink, { ENTITY_TYPE } from '../common/HyperLink';

const { Content } = Layout;

const EquipmentPerStockLocationDetailsDetailPage: FC<any> = ({
	epsl,
	fetching,
	currentUser,
}): ReactElement => {
	const [editModalVisible, setEditModalVisible] = useState(false);

	const showEditModal = useCallback(() => setEditModalVisible(true), []);
	const hideEditModal = useCallback(() => setEditModalVisible(false), []);

	const epslDetail = useMemo(
		() => ({
			'Equipment ID': nullSafeGetOrElse('equipmentUniqueId', epsl, '--'),
			'Equipment Type ID': nullSafeGetOrElse('equipment.equipmentTypeId', epsl, '--'),
			'Equipment Type': (
				<HyperLink
					text={nullSafeGetOrElse('equipment.modelName', epsl, '--')}
					entityType={ENTITY_TYPE.EQUIPMENT}
					entityId={nullSafeGetOrElse('equipment.id', epsl, '--')}
					backLinkText="Back to Equipment type detail"
				/>
			),
			'Stock Location': nullSafeGetOrElse('stockLocation.name', epsl, '--'),
			'Serial Number': (
				<div>
					{nullSafeGetOrElse('serialNumber', epsl, '--')}
					<Button type="link" icon={<EditOutlined translate="" onClick={showEditModal} />} />
				</div>
			),
			...(nullSafeGet('cost', epsl) && {
				Cost: renderCurrency(currentUser)(epsl.cost),
			}),
		}),
		[currentUser, epsl, showEditModal]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Location Name',
				dataIndex: 'locationId',
				render: (_, record) =>
					nullSafeGet('location.name', record) || nullSafeGet('stockLocation.name', record) || '__',
			},
			{
				title: 'Usage',
				dataIndex: 'locationId',
				render: (_, record) => (record.locationId ? 'In Use' : 'At warehouse'),
			},
			{
				title: 'From date',
				dataIndex: 'markedTransferredToCurrentLocationAt',
				render: (val) => (val ? dateFormatter(val, DATE_FORMAT) : '--'),
			},
			{
				title: 'To date',
				dataIndex: 'markedTransferredFromCurrentLocationAt',
				render: (val) => (val ? dateFormatter(val, DATE_FORMAT) : '--'),
			},
		],
		[]
	);

	return (
		<Content
			style={{
				padding: '0 0.5em',
			}}
		>
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				<Col span={16}>
					<Card loading={fetching} title="Details">
						<HorizontalKeyValueDisplay rowStyle={{ minWidth: '25%' }} keyValueStore={epslDetail} />
					</Card>
				</Col>
				<Col span={8}>
					<Card loading={fetching} title={epsl.asset ? 'Associated Asset' : ''}>
						{epsl.asset ? <AssetCardDisplay asset={epsl.asset} /> : 'No associated Asset Found'}
					</Card>
				</Col>
			</Row>
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				<Col span={24}>
					<Card bodyStyle={{ padding: 8 }} title="Transfer History" loading={fetching}>
						<PaginatedReduxTable
							mode="list"
							emptyState={
								<EmptyState
									graphic={
										<img
											alt="No transfer history found"
											style={{ marginBottom: 8 }}
											src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
										/>
									}
									headline={'No transfer history found'}
									body={null}
								/>
							}
							collectionName="asset_history"
							targetCollectionName="assetHistoryIndex"
							columns={columns}
							showHeader
							keyAccessor={(el) => el.id}
							initialFilters={{
								equipmentPerStockLocationId: epsl.id,
							}}
							fetchData={assetHistoryRestCrudThunksForSupplier.read}
						/>
					</Card>
				</Col>
			</Row>
			{editModalVisible && <EditSerialNumberModal onCancel={hideEditModal} />}
		</Content>
	);
};

const mapStateToProps = (state) => ({
	fetching: state.equipment_per_stock_locations.fetching,
	epsl: state.equipment_per_stock_locations.detail,
	currentUser: state.session.currentUser,
});

const ComponentWithoutUserType = withRouter<any, any>(
	connect(mapStateToProps)(EquipmentPerStockLocationDetailsDetailPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
